// The Super Admin page's API (DEC-204, SDD §40.5). Every route answers 404 to anyone
// who is not a Super Admin, so this module is only called from the Super Admin page.

import { http } from './client'

const BASE = '/api/v1/super-admin'

export interface WorkspaceSummary {
  tenant_id: string
  name: string
  first_admin_email: string | null
  members: number
  created_at: string | null
  created_via: string | null
  plan: string
  scans_today: number
  daily_scan_limit: number | null
  max_concurrent_scans: number
  max_concurrent_scans_set: number | null
  min_scan_interval_minutes: number
  min_scan_interval_minutes_set: number | null
  last_scan_at: string | null
  disabled_at: string | null
  is_own: boolean
}

export interface WorkspaceList {
  workspaces: WorkspaceSummary[]
  default_daily_scan_limit: number | null
}

export interface WorkspaceUpdate {
  daily_scan_limit?: number
  max_concurrent_scans?: number
  min_scan_interval_minutes?: number
  disabled?: boolean
  clear?: Array<'daily_scan_limit' | 'max_concurrent_scans' | 'min_scan_interval_minutes'>
}

export interface WorkspaceCreated {
  workspace: WorkspaceSummary
  invite_url: string
  email_sent: boolean
  email_notice: string | null
}

export interface SignupDay {
  date: string
  workspaces: number
  self_serve: number
}

export interface SignupsResponse {
  days: SignupDay[]
  total: number
  self_serve_total: number
}

export interface AuditEntry {
  at: string
  actor_email: string
  action: string
  tenant_id: string | null
  workspace_name: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}

/** DEC-205: a website sign-up waiting for (or past) a Super Admin's decision. */
export interface SignupRequest {
  signup_id: string
  email: string
  email_domain: string
  workspace_name: string | null
  name: string | null
  source: string
  status: 'pending' | 'approved' | 'declined'
  requested_at: string | null
  /** Requests ever submitted from this email domain, this one included. */
  from_domain: number
  decided_by: string | null
  decided_at: string | null
  decline_note: string | null
  tenant_id: string | null
}

export interface SignupRequestList {
  requests: SignupRequest[]
  pending_count: number
}

export const superAdminApi = {
  listWorkspaces: (): Promise<WorkspaceList> => http.get(`${BASE}/workspaces`),
  updateWorkspace: (tenantId: string, body: WorkspaceUpdate): Promise<WorkspaceSummary> =>
    http.patch(`${BASE}/workspaces/${encodeURIComponent(tenantId)}`, body),
  createWorkspace: (name: string, adminEmail: string): Promise<WorkspaceCreated> =>
    http.post(`${BASE}/workspaces`, { name, admin_email: adminEmail }),
  getDefaults: (): Promise<{ default_daily_scan_limit: number | null }> => http.get(`${BASE}/defaults`),
  setDefaults: (value: number | null): Promise<{ default_daily_scan_limit: number | null }> =>
    http.patch(`${BASE}/defaults`, { default_daily_scan_limit: value }),
  signups: (days = 30): Promise<SignupsResponse> => http.get(`${BASE}/signups?days=${days}`),
  audit: (limit = 50): Promise<{ entries: AuditEntry[] }> => http.get(`${BASE}/audit?limit=${limit}`),
  signupRequests: (status: 'pending' | 'approved' | 'declined' | 'all' = 'pending'): Promise<SignupRequestList> =>
    http.get(`${BASE}/signup-requests?status=${status}`),
  approveSignup: (signupId: string): Promise<{ request: SignupRequest; workspace: WorkspaceSummary; email_sent: boolean }> =>
    http.post(`${BASE}/signup-requests/${encodeURIComponent(signupId)}/approve`, {}),
  declineSignup: (signupId: string, note: string | null): Promise<SignupRequest> =>
    http.post(`${BASE}/signup-requests/${encodeURIComponent(signupId)}/decline`, { note }),
}
